import * as React from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {
  GridRowModes,
  DataGrid,
  useGridApiRef,
  GridRowEditStopReasons,
  GridToolbar
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
  Grid,
  Paper
} from "@mui/material";
import {GET, PATCH, BASE_URL} from "APIclient";
import Snackbar, {SnackbarCloseReason} from "@mui/material/Snackbar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useNavigate} from "react-router-dom";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {useEffect, useState} from "react";
import dayjs from "dayjs";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import * as XLSX from "xlsx";
import properties from "constants/ApplicationConstants";
const rowLength = 20;

export default function LwpReport() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);
  const [empId, setEmpId] = React.useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [filteredData, setFilteredData] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setSize] = React.useState(rowLength);

  const [formData, setFormData] = useState({
    startDate: dayjs(),
    endDate: dayjs()
  });

  const columns = [
    {field: "id", headerName: "#", flex: 0.5, align: "center", headerAlign: "center"},
    {field: "empId", headerName: "EMP ID", flex: 0.5, align: "center", headerAlign: "center"},
    {
      field: "empName",
      headerName: "Employee Name",
      flex: 2,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "lwpLeaves",
      headerName: "LWP Leaves",
      flex: 1,
      align: "center",
      headerAlign: "center"
    }
  ];

  const fetchData = async (page, pageSize, empId, startDate, endDate) => {
    try {
      setLoading(true);
      const response = await GET(
        `admin/lwp-report?page=${page}&size=${pageSize}&empId=${empId}&startDate=${startDate}&endDate=${endDate}`
      );
      const data = response?.data?.data;
      // const totalRecords = response?.data?.data?.totalItems;
      // const totalPages = response?.data?.data?.totalPages;
      setData(data);
      setFilteredData(data);
      setTotalPages(totalPages);
      setTotalRows(totalRecords);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const apiRef = useGridApiRef();

  useEffect(() => {
    fetchData(
      page,
      pageSize,
      empId,
      formData.startDate?.format("YYYY-MM-DD"),
      formData.endDate?.format("YYYY-MM-DD")
    );
  }, [page, pageSize, empId, startDate, endDate]);

  const handleDateChange = (date, field) => {
    const validDate = date ? dayjs(date) : null;
    setFormData(prev => ({
      ...prev,
      [field]: validDate?.isValid() ? validDate : null
    }));
  };

  const handleSearch = async () => {
    await fetchData(
      page,
      pageSize,
      empId,
      formData.startDate?.format("YYYY-MM-DD"),
      formData.endDate?.format("YYYY-MM-DD")
    );
  };

  const onSearch = e => {
    const {value, name} = e.target;
    if (name === "empId") {
      setEmpId(value || "");
    }
  };

  const handlePaginationChange = newPage => {
    setPage(newPage);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LWP Reports");
    XLSX.writeFile(workbook, "LWP_Report.xlsx");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                <Typography
                    sx={{
                      gridColumn: "span 2",
                      color: "textPrimary",
                      fontFamily: properties.fontFamily,
                      fontSize: "15px",
                      fontWeight: "bold"
                    }}
                >
                  LWP Report
                </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <MDInput
                    fullWidth
                    value={empId}
                    onChange={onSearch}
                    name="empId"
                    label="Employee ID"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{width: "100%"}}
                      size="small"
                      label="Start Date"
                      name="startDate"
                      value={startDate || null}
                      onChange={date => handleDateChange(date, "startDate")}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{width: "100%"}}
                      size="small"
                      label="End Date"
                      name="endDate"
                      value={endDate || null}
                      onChange={date => handleDateChange(date, "endDate")}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6} md={1.5}>
                  <MDButton
                    fullWidth
                    sx={{background: properties.colors.bgcolorbluethem}}
                    variant="contained"
                    color="info"
                    onClick={handleSearch}
                  >
                    Search
                  </MDButton>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                  <MDButton
                    fullWidth
                    variant="gradient"
                    color="success"
                    onClick={handleDownloadExcel}
                  >
                    Download
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredData.map((row, idx) => ({...row, id: idx + 1 + page * pageSize}))}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[rowLength]}
              pagination
              page={page}
              onPageChange={handlePaginationChange}
              loading={loading}
              getRowId={row => row.empId}
              components={{Toolbar: GridToolbar}}
              apiRef={apiRef}
              sx={{
                height: "500px",
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight:400,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  width: "100%",
                  textAlign: "center"
                },
                "& .MuiDataGrid-columnHeader": {
                  justifyContent: "center"
                },
                "& .MuiDataGrid-footerContainer": {
                  justifyContent: "flex-start"
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "auto"
                },
              }}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
